import { Component, OnDestroy, OnInit, Signal, WritableSignal } from '@angular/core';
import { SidebarService } from '@services/sidebar/sidebar.service';
import { Subscription } from 'rxjs';
import { AuthService } from '@services/auth/auth.service';
import { TranslateModule } from '@ngx-translate/core';
import { AdminService } from '@services/admin/admin.service';
import { DashboardOwnerUser, AuthenticatedUser } from '@dashboard_models/dashboard-owner-user';
import { LocalStorageService } from '@services/local-storage/local-storage.service';
import { SearchBarComponent } from '@components/search/search-bar/search-bar.component';
import { SearchService } from '@services/search/search-service';
import { RouterLink } from '@angular/router';
import { HelperService } from '@services/helper/helper.service';
import dayjs from 'dayjs';
import { environment } from '../../../environments/environment';
import { MaintenanceService } from '@services/auth/maintenance.service';
import { AwTooltipComponent } from '@components/misc/aw-tooltip/aw-tooltip.component';
import { OwnerSelectorComponent } from '../nav-sidebar/owner-selector/owner-selector.component';
import { SamlService } from '@services/auth/saml.service';

@Component({
    selector: 'app-topbar',
    imports: [TranslateModule, SearchBarComponent, RouterLink, AwTooltipComponent, OwnerSelectorComponent],
    templateUrl: './topbar.component.html',
    styleUrl: './topbar.component.scss'
})
export class TopbarComponent implements OnInit, OnDestroy {
    isAdmin: Signal<boolean> = this.authService.isAdmin;
    authenticatedUser: AuthenticatedUser = this.localStorageService.getItem('authenticatedUser');
    dashboardOwnerUser: DashboardOwnerUser = this.helperService.getDashboardOwnerUser();

    displayUpdateBar: boolean;
    minorUpdate: boolean;
    majorUpdate: boolean;
    secondsToSignOut = 15;
    isDemo = environment.isDemo;
    versionSub: Subscription;

    mobileSearchOpen: WritableSignal<boolean> = this.searchService.mobileSearchOpen;

    constructor(
        protected sidebarService: SidebarService,
        private authService: AuthService,
        protected adminService: AdminService,
        private localStorageService: LocalStorageService,
        private searchService: SearchService,
        protected maintenanceService: MaintenanceService,
        private helperService: HelperService,
        protected samlService: SamlService
    ) {}

    ngOnInit() {
        this.setVersionSub();
    }

    ngOnDestroy(): void {
        if (this.versionSub) this.versionSub.unsubscribe();
    }

    setVersionSub() {
        // Subscribe on the version number of the dashboard
        // If it sees that the version it is subscribed to does not match the one in
        // the environment file, it will enable a top-bar with a reload button in.
        console.log('Trying to set version subscription...');
        this.versionSub = this.helperService.getCurrentDashboardVersion().subscribe(version => {
            const fetchedVersionParts: number[] | null = parseVersion(version.snapshot.val() as string);
            const projectVersionParts: number[] | null = parseVersion(environment.VERSION);

            if (!fetchedVersionParts || !projectVersionParts) {
                console.error('Failed to parse version information.');
                this.resetUpdateFlags();
                return;
            }

            if (isMajorUpdate(fetchedVersionParts, projectVersionParts)) {
                this.handleMajorUpdate();
            } else if (isMinorUpdate(fetchedVersionParts, projectVersionParts)) {
                this.handleMinorUpdate();
            } else {
                this.resetUpdateFlags();
            }
        });

        const parseVersion = (version: string): number[] | null => {
            const parts = version.split('.').map(part => parseInt(part, 10));
            return parts.length === 3 && parts.every(part => !isNaN(part)) ? parts : null;
        };

        const isMajorUpdate = (fetched: number[], current: number[]): boolean => {
            return fetched[0] > current[0] || (fetched[0] === current[0] && fetched[1] > current[1]);
        };

        const isMinorUpdate = (fetched: number[], current: number[]): boolean => {
            return fetched[0] === current[0] && fetched[1] === current[1] && fetched[2] > current[2];
        };
    }

    handleMajorUpdate(): void {
        this.majorUpdate = true;
        this.minorUpdate = false;

        if (!this.helperService.unguardedUrl(window.location.pathname)) {
            this.displayUpdateBar = true;
            setTimeout(() => {
                this.displayUpdateBar = false;
                this.authService.signOut();
            }, 15 * 1000);
        } else {
            this.refreshWindow();
            this.displayUpdateBar = false;
        }
    }

    handleMinorUpdate(): void {
        this.minorUpdate = true;
        this.majorUpdate = false;
        this.displayUpdateBar = true;
    }

    resetUpdateFlags(): void {
        this.majorUpdate = false;
        this.minorUpdate = false;
        this.displayUpdateBar = false;
    }

    refreshWindow() {
        // is used in HTML
        const urlParams = new URLSearchParams(window.location.search);
        const updatedParam = urlParams.get('updated');
        if (updatedParam) {
            const updatedTime = parseInt(updatedParam);
            const currentTime = dayjs().unix();
            if (currentTime - updatedTime < 10) {
                // if the time since last update is less than 10 seconds, do not refresh
                this.resetUpdateFlags(); // reset the flags
                setTimeout(() => {
                    this.setVersionSub(); // check for updates again
                }, 10000);
                return;
            } else {
                // making the URL updated param that will trigger a refresh, this is more effective than a simple window.location.reload()
                urlParams.set('updated', dayjs().unix().toString());
                window.location.search = urlParams.toString();
            }
        } else {
            // making the URL updated param that will trigger a refresh
            urlParams.set('updated', dayjs().unix().toString());
            window.location.search = urlParams.toString();
        }
    }
}
